import React from "react";
import classNames from "classnames";
import { Transforms } from "slate";
import {
    ReactEditor,
    RenderElementProps,
    RenderLeafProps,
    useFocused,
    useSelected,
    useSlateStatic,
} from "slate-react";
import { WorkspaceUserAssignment } from "@common/types/workspaceUserAssignment";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { CheckboxIndicator } from "@design-system/Checkbox/CheckboxIndicator";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { getFullName, isChecklistItem } from "./slateHelpers";
import {
    EntityRefElement,
    LinkElement,
    ListItemElement,
    MentionElement,
} from "./slateTypes";
import { EntityRefPill } from "./entityRef/EntityRefPill";
import styles from "./SlateEditor.module.scss";

export const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
    let content = children;
    if (leaf.bold) {
        content = <strong>{content}</strong>;
    }
    if (leaf.italic) {
        content = <em>{content}</em>;
    }
    if (leaf.underline) {
        content = <u>{content}</u>;
    }
    return <span {...attributes}>{content}</span>;
};

const Mention = ({
    attributes,
    children,
    element,
    workspaceMembers,
}: RenderElementProps & {
    element: MentionElement;
    workspaceMembers?: WorkspaceUserAssignment[];
}) => {
    const selected = useSelected();
    const focused = useFocused();

    const member = workspaceMembers?.find((m) => m.userId === element.userId);
    const user = member?.user;
    const fullName = getFullName(user, element.name);

    return (
        <Tooltip
            content={
                <div className={styles.mentionTooltip}>
                    {user && <UserAvatar user={user} size="sm" aria-hidden />}
                    <span>{fullName}</span>
                </div>
            }
        >
            <span
                {...attributes}
                contentEditable={false}
                className={classNames(styles.mention, {
                    [styles.mentionSelected]: selected && focused,
                })}
                data-user-id={element.userId}
            >
                <span contentEditable={false}>
                    @{element.name}
                    {children}
                </span>
            </span>
        </Tooltip>
    );
};

const EntityRefNode = ({
    attributes,
    children,
    element,
}: RenderElementProps & { element: EntityRefElement }) => {
    const selected = useSelected();
    const focused = useFocused();

    return (
        <EntityRefPill
            entityRef={element}
            attributes={attributes}
            isSelected={selected && focused}
        >
            {children}
        </EntityRefPill>
    );
};

const LinkNode = ({
    attributes,
    children,
    element,
}: RenderElementProps & { element: LinkElement }) => (
    <a
        {...attributes}
        href={element.url}
        className={styles.link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
            if (!e.metaKey && !e.ctrlKey) {
                e.preventDefault();
            }
        }}
    >
        {children}
    </a>
);

const ListItem = ({
    attributes,
    children,
    element,
}: RenderElementProps & { element: ListItemElement }) => {
    const editor = useSlateStatic();
    if (!isChecklistItem(element)) {
        return <li {...attributes}>{children}</li>;
    }

    return (
        <li {...attributes} className={styles.checkItem}>
            <span
                contentEditable={false}
                className={styles.checkControl}
                onMouseDown={(event) => {
                    event.preventDefault();
                    Transforms.setNodes(
                        editor,
                        { checked: !element.checked },
                        { at: ReactEditor.findPath(editor, element) },
                    );
                }}
            >
                <CheckboxIndicator
                    state={element.checked ? "checked" : "unchecked"}
                />
            </span>
            {children}
        </li>
    );
};

export const Element = (
    props: RenderElementProps & {
        workspaceMembers?: WorkspaceUserAssignment[];
    },
) => {
    const { attributes, children, element, workspaceMembers } = props;
    if (element.type === "mention") {
        return (
            <Mention
                {...props}
                element={element}
                workspaceMembers={workspaceMembers}
            />
        );
    }
    if (element.type === "entityRef") {
        return <EntityRefNode {...props} element={element} />;
    }
    if (element.type === "link") {
        return <LinkNode {...props} element={element} />;
    }
    if (element.type === "bulleted-list") {
        return <ul {...attributes}>{children}</ul>;
    }
    if (element.type === "numbered-list") {
        return <ol {...attributes}>{children}</ol>;
    }
    if (element.type === "list-item") {
        return <ListItem {...props} element={element} />;
    }
    return <p {...attributes}>{children}</p>;
};
